/**
 * Os índices regulatórios do backoffice (`OB-05`, ADR-069), dentro de Indicadores.
 *
 * Para um mês: **cada índice está dentro da meta?** — a leitura de placa, a confiabilidade do
 * processamento, a passagem disponível para pagar em 2 h e em 24 h, a disponibilidade dos
 * pórticos e, onde o contrato pede, o aproveitamento dos registros de infração. Por concessão e
 * por pórtico.
 *
 * - **A tela não calcula nada.** O percentual vem truncado do servidor (nunca mostra a meta
 *   alcançada sem ter sido), a meta vem do regime — ou da Res. ANTT nº 6.079/2026, dita como
 *   referência —, e a situação também.
 * - **Toda falta abre a fila que a produziu** (`AC14`): a SENATRAN com o recorte, o mês até o
 *   corte da base, a concessão e o pórtico.
 * - **A disponibilidade sai das inoperâncias**: a suspeita que o cockpit abre quando o pórtico ao
 *   vivo fica em silêncio, e o que uma pessoa registra. A supervisão confirma (com a causa e,
 *   quando a causa tira o tempo da conta, a evidência), descarta, e registra o aviso ao
 *   regulador da falha crítica — o cockpit não fala com o regulador.
 * - **A tela relê a cada minuto**, e a resposta que chega depois de uma troca de filtro é
 *   descartada.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ConcessaoNosIndices,
  IndiceNaTela,
  IndicesRegulatorios as Visao,
  InoperanciaNaTela,
  OpcaoDosIndices,
  PorticoNosIndices,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { dataCurta, dataEHora, numeroLongo, restante } from "../formato";
import { useDeslocamento } from "../relogio";
import {
  COLUNA_DO_INDICE,
  COR_DA_SITUACAO,
  ROTULO_DA_INOPERANCIA,
  avisoPendente,
  duracao,
  enderecoDaFalta,
  metaDoIndice,
  paraOCampo,
  paraOServidor,
  percentualDoIndice,
  podeAvisar,
} from "./indices";
import { repetirComAbaVisivel } from "../ganchos/repetirComAbaVisivel";

/** De quanto em quanto a tela relê. */
export const RELEITURA_MS = 60_000;

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

/** O que o formulário da inoperância está fazendo agora. */
type Edicao =
  | { tipo: "registrar" }
  | { tipo: "confirmar"; alvo: InoperanciaNaTela }
  | { tipo: "descartar"; alvo: InoperanciaNaTela }
  | { tipo: "avisar"; alvo: InoperanciaNaTela };

export function IndicesRegulatorios() {
  const [parametros, definirParametros] = useSearchParams();
  const periodo = parametros.get("periodo") ?? "";
  const concessao = parametros.get("concessao_id") ?? "";
  const [visao, definirVisao] = useState<Visao | null>(null);
  const [erro, definirErro] = useState<{
    texto: string;
    semLicenca: boolean;
  } | null>(null);
  const [tentativa, definirTentativa] = useState(0);
  const [edicao, definirEdicao] = useState<Edicao | null>(null);
  const navegar = useNavigate();
  const deslocamento = useDeslocamento();

  const ultima = useRef(0);
  const carregar = useCallback(async () => {
    const esta = ++ultima.current;
    const valida = () => esta === ultima.current;
    try {
      const p = new URLSearchParams();
      if (periodo) p.set("periodo", periodo);
      if (concessao) p.set("concessao_id", concessao);
      const lida = await api<Visao>(`/tollvision/indices?${p.toString()}`);
      if (!valida()) return;
      definirVisao(lida);
      definirErro(null);
    } catch (falha) {
      if (valida())
        definirErro({
          texto: mensagem(falha, "Não foi possível ler os índices."),
          semLicenca: falha instanceof ErroDaApi && falha.semLicenca,
        });
    }
  }, [periodo, concessao]);

  useEffect(() => {
    void deslocamento;
    void tentativa;
    void carregar();
    const parar = repetirComAbaVisivel(() => void carregar(), RELEITURA_MS);
    return () => {
      ultima.current++;
      parar();
    };
  }, [carregar, deslocamento, tentativa]);

  function escolher(chave: "periodo" | "concessao_id", valor: string) {
    const p = new URLSearchParams(parametros);
    if (valor) p.set(chave, valor);
    else p.delete(chave);
    definirParametros(p);
  }

  // Relê pelo efeito, com o filtro de agora — e não com o `carregar` de quando o formulário
  // abriu: a troca de mês no meio da gravação não pode trazer de volta o mês de antes.
  function depoisDeGravar() {
    definirEdicao(null);
    definirTentativa((n) => n + 1);
  }

  const porticosEmSuspeita = new Set(
    visao?.inoperancias
      .filter((x) => x.situacao === "SUSPEITA")
      .map((x) => x.praca_id) ?? [],
  ).size;
  const pendentes = visao?.inoperancias.filter(avisoPendente) ?? [];

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Índices regulatórios
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            As metas do regulador, por mês e por pórtico — e cada falta abre a
            fila que a produziu
          </p>
        </div>
        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <select
            aria-label="Concessão"
            value={concessao}
            onChange={(e) => escolher("concessao_id", e.target.value)}
            className="min-h-8 max-w-full min-w-0 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
          >
            <option value="">Todas as concessões</option>
            {visao?.concessoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <select
            aria-label="Mês"
            value={periodo || visao?.periodo.codigo || ""}
            onChange={(e) => escolher("periodo", e.target.value)}
            className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
          >
            {visao?.periodos.map((p) => (
              <option key={p.codigo} value={p.codigo}>
                {p.rotulo}
                {p.fechado ? "" : " (em curso)"}
              </option>
            ))}
          </select>
          {visao?.pode_decidir && (
            <Botao
              papel="primaria"
              onClick={() => definirEdicao({ tipo: "registrar" })}
            >
              <Icone.Alerta />
              Registrar inoperância
            </Botao>
          )}
          <Link
            to="/indicadores"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            ‹ Voltar aos Indicadores
          </Link>
        </div>
      </div>

      {erro && !visao ? (
        <Painel titulo="Índices">
          <Estado
            tipo={erro.semLicenca ? "sem-licenca" : "erro"}
            titulo={erro.texto}
            acao={
              <Botao
                onClick={() => {
                  definirErro(null);
                  definirTentativa((n) => n + 1);
                }}
              >
                Tentar de novo
              </Botao>
            }
          />
        </Painel>
      ) : !visao ? (
        <Painel titulo="Índices">
          <Estado tipo="carregando" titulo="Contando os índices do mês…" />
        </Painel>
      ) : visao.resultado.length === 0 ? (
        <Painel titulo="Índices">
          <Estado
            tipo="vazio"
            titulo="Nenhuma concessão no seu recorte."
            detalhe="Os índices são por concessão: peça ao administrador o acesso à concessão que você acompanha."
          />
        </Painel>
      ) : (
        <div className="grid min-h-0 auto-rows-max content-start gap-3 overflow-y-auto pb-1">
          {erro && (
            <p
              role="alert"
              className="rounded-cartao border border-vermelho-borda bg-vermelho-fundo px-3 py-2 text-[11.5px] text-tinta"
            >
              Não foi possível atualizar: {erro.texto} Os números abaixo são da
              última leitura.
            </p>
          )}
          <Avisos
            visao={visao}
            porticosEmSuspeita={porticosEmSuspeita}
            pendentes={pendentes}
          />
          {edicao && (
            <FormularioDaInoperancia
              // Um formulário por alvo: o estado de um não passa para o outro.
              key={`${edicao.tipo}:${edicao.tipo === "registrar" ? "novo" : edicao.alvo.id}`}
              edicao={edicao}
              visao={visao}
              aoCancelar={() => definirEdicao(null)}
              aoGravar={depoisDeGravar}
            />
          )}
          {visao.resultado.map((c) => (
            <IndicesDaConcessao
              key={c.id}
              concessao={c}
              desde={visao.periodo.inicio}
              aoAbrir={(endereco) => navegar(endereco)}
            />
          ))}
          <Inoperancias
            visao={visao}
            aoEditar={visao.pode_decidir ? definirEdicao : null}
          />
        </div>
      )}
    </>
  );
}

function Avisos({
  visao,
  porticosEmSuspeita,
  pendentes,
}: {
  visao: Visao;
  porticosEmSuspeita: number;
  pendentes: InoperanciaNaTela[];
}) {
  const criticos = pendentes.map(
    (x) =>
      `${x.praca}: falha crítica sem o aviso ao regulador registrado — o prazo ${
        restante(x.avisar_ate!) === "vencido"
          ? "já venceu"
          : `vence às ${dataCurta(x.avisar_ate!)} (${restante(x.avisar_ate!)})`
      }.`,
  );
  const avisos: string[] = [];
  if (porticosEmSuspeita > 0) {
    avisos.push(
      porticosEmSuspeita === 1
        ? "Um pórtico em silêncio fora do habitual espera a supervisão confirmar ou descartar a inoperância."
        : `${numeroLongo(porticosEmSuspeita)} pórticos em silêncio fora do habitual esperam a supervisão confirmar ou descartar a inoperância.`,
    );
  }
  if (!visao.periodo.fechado) {
    avisos.push(
      `${visao.periodo.rotulo} ainda está em curso: os números andam até o fim do mês, e a disponibilidade para pagar só conta as passagens cuja janela já fechou.`,
    );
  }
  return (
    <>
      {criticos.length > 0 && (
        <ListaDeAvisos
          rotulo="Avisos críticos dos índices"
          avisos={criticos}
          cor="vermelho"
        />
      )}
      {avisos.length > 0 && (
        <ListaDeAvisos
          rotulo="Avisos dos índices"
          avisos={avisos}
          cor="amarelo"
        />
      )}
    </>
  );
}

function ListaDeAvisos({
  rotulo,
  avisos,
  cor,
}: {
  rotulo: string;
  avisos: string[];
  cor: "amarelo" | "vermelho";
}) {
  return (
    <ul
      aria-label={rotulo}
      className={`grid gap-1 rounded-cartao border px-3 py-2 text-[11.5px] text-tinta ${
        cor === "vermelho"
          ? "border-vermelho-borda bg-vermelho-fundo"
          : "border-amarelo-borda bg-amarelo-fundo"
      }`}
    >
      {avisos.map((a) => (
        <li key={a} className="flex items-start gap-1.5">
          <span
            className={cor === "vermelho" ? "text-vermelho" : "text-amarelo"}
          >
            <Icone.Alerta />
          </span>
          {a}
        </li>
      ))}
    </ul>
  );
}

function IndicesDaConcessao({
  concessao,
  desde,
  aoAbrir,
}: {
  concessao: ConcessaoNosIndices;
  desde: string;
  aoAbrir: (endereco: string) => void;
}) {
  return (
    <Painel
      titulo={`${concessao.nome} · ${concessao.regime}`}
      rodape={
        <span>
          A meta é a do regime da concessão; sem ela, a da Res. ANTT nº
          6.079/2026, como referência.
          {concessao.aviso_de_falha ? ` ${concessao.aviso_de_falha}` : ""}
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {concessao.indices.map((i) => (
          <CartaoDoIndice
            key={i.indice}
            indice={i}
            endereco={enderecoDaFalta(i, desde, concessao.id)}
            aoAbrir={aoAbrir}
          />
        ))}
      </div>
      <TabelaDosPorticos
        concessao={concessao}
        desde={desde}
        aoAbrir={aoAbrir}
      />
    </Painel>
  );
}

function CartaoDoIndice({
  indice: i,
  endereco,
  aoAbrir,
}: {
  indice: IndiceNaTela;
  endereco: string | null;
  aoAbrir: (endereco: string) => void;
}) {
  const cor = COR_DA_SITUACAO[i.situacao];
  const borda =
    cor === "vermelho"
      ? "border-vermelho-borda bg-vermelho-fundo"
      : cor === "amarelo"
        ? "border-amarelo-borda bg-amarelo-fundo"
        : "border-linha bg-painel-2";
  const numero =
    cor === "vermelho"
      ? "text-vermelho"
      : cor === "amarelo"
        ? "text-amarelo"
        : cor === "verde"
          ? "text-verde"
          : "text-tinta";
  return (
    <section
      aria-label={i.rotulo}
      title={i.definicao}
      className={`grid content-start gap-1 rounded-cartao border p-2.5 ${borda}`}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="text-[11.5px] leading-tight text-tinta-3">
          {i.rotulo}
        </span>
        <Badge cor={cor}>{i.situacao_rotulo}</Badge>
      </span>
      <b className={`mono text-[24px] leading-none font-extrabold ${numero}`}>
        {i.situacao === "SEM_FONTE" ? "—" : percentualDoIndice(i.percentual)}
      </b>
      <span className="text-[10.5px] text-tinta-3">
        {metaDoIndice(i)}
        {i.situacao !== "SEM_FONTE" && i.situacao !== "SEM_BASE" && (
          <>
            {" · "}
            {i.unidade === "segundos"
              ? `${duracao(i.base - i.numerador)} parado`
              : `${numeroLongo(i.numerador)} de ${numeroLongo(i.base)}`}
          </>
        )}
      </span>
      {endereco && (
        <button
          type="button"
          onClick={() => aoAbrir(endereco)}
          className="justify-self-start text-[11px] font-semibold text-azul hover:underline"
        >
          {numeroLongo(i.faltas)} fora — abrir a fila
        </button>
      )}
      {i.nota && (
        <span className="text-[10.5px] leading-snug text-tinta-3">
          {i.nota}
        </span>
      )}
      {i.meta && (
        <span className="text-[10px] leading-snug text-tinta-3">
          {i.meta.fonte}
        </span>
      )}
    </section>
  );
}

function TabelaDosPorticos({
  concessao,
  desde,
  aoAbrir,
}: {
  concessao: ConcessaoNosIndices;
  desde: string;
  aoAbrir: (endereco: string) => void;
}) {
  const colunas = concessao.porticos[0]?.indices.map((i) => i.indice) ?? [];
  if (concessao.porticos.length === 0) {
    return (
      <p className="text-[11.5px] text-tinta-3">
        Nenhum pórtico ativo nesta concessão.
      </p>
    );
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <table
        aria-label={`Índices por pórtico — ${concessao.nome}`}
        className="w-full min-w-[760px] border-collapse text-[11.5px]"
      >
        <thead className="bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
          <tr>
            <th className="px-2 py-1.5">Pórtico</th>
            {colunas.map((c) => (
              <th key={c} className="px-2 py-1.5 text-right">
                {COLUNA_DO_INDICE[c]}
              </th>
            ))}
            <th className="px-2 py-1.5 text-right">Parado no mês</th>
          </tr>
        </thead>
        <tbody>
          {concessao.porticos.map((p) => (
            <LinhaDoPortico
              key={p.praca_id}
              portico={p}
              concessao={concessao.id}
              desde={desde}
              aoAbrir={aoAbrir}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LinhaDoPortico({
  portico: p,
  concessao,
  desde,
  aoAbrir,
}: {
  portico: PorticoNosIndices;
  concessao: string;
  desde: string;
  aoAbrir: (endereco: string) => void;
}) {
  return (
    <tr className="border-b border-linha-suave hover:bg-realce">
      <td className="px-2 py-1.5 text-tinta">
        <span className="font-semibold">{p.praca}</span>
        <span className="ml-1.5 text-[10.5px] text-tinta-3">{p.codigo}</span>
        {p.parado_agora && (
          <span className="ml-1.5">
            <Badge cor="vermelho">parado agora</Badge>
          </span>
        )}
      </td>
      {p.indices.map((i) => {
        const endereco = enderecoDaFalta(i, desde, concessao, p.praca_id);
        const cor = COR_DA_SITUACAO[i.situacao];
        const texto = percentualDoIndice(i.percentual);
        const classe = `mono ${
          cor === "vermelho"
            ? "text-vermelho font-bold"
            : cor === "amarelo"
              ? "text-amarelo font-bold"
              : "text-tinta-2"
        }`;
        return (
          <td key={i.indice} className="px-2 py-1.5 text-right">
            {endereco ? (
              <button
                type="button"
                title={`${i.situacao_rotulo} — ${numeroLongo(i.faltas)} fora: abrir a fila deste pórtico`}
                onClick={() => aoAbrir(endereco)}
                className={`${classe} hover:underline`}
              >
                {texto}
                <span className="sr-only"> — {i.situacao_rotulo}</span>
              </button>
            ) : (
              <span className={classe} title={i.situacao_rotulo}>
                {texto}
                <span className="sr-only"> — {i.situacao_rotulo}</span>
              </span>
            )}
          </td>
        );
      })}
      <td className="mono px-2 py-1.5 text-right text-tinta-2">
        {p.inoperante_s > 0 ? duracao(p.inoperante_s) : "—"}
        {p.excluido_s > 0 && (
          <span
            className="block text-[10px] text-tinta-3"
            title="Inoperância causada pelo que a resolução exclui: registrada, e fora da conta."
          >
            +{duracao(p.excluido_s)} excluído
          </span>
        )}
        {p.em_suspeita_s > 0 && (
          <span className="block text-[10px] text-amarelo">
            +{duracao(p.em_suspeita_s)} em suspeita
          </span>
        )}
      </td>
    </tr>
  );
}

function Inoperancias({
  visao,
  aoEditar,
}: {
  visao: Visao;
  aoEditar: ((e: Edicao) => void) | null;
}) {
  return (
    <Painel
      titulo={`Inoperâncias — ${visao.periodo.rotulo} (${visao.inoperancias.length})`}
      rolagemPropria
      rodape={
        <span>
          A suspeita é do cockpit, pelo silêncio do pórtico ao vivo; só a
          confirmada conta na disponibilidade. Nada se apaga: a que não era se
          descarta, com o motivo.
        </span>
      }
    >
      {visao.inoperancias.length === 0 ? (
        <Estado
          tipo="vazio"
          titulo="Nenhuma inoperância no mês."
          detalhe="Quando um pórtico ao vivo fica em silêncio fora do habitual, a suspeita aparece aqui."
        />
      ) : (
        <div className="min-h-0 overflow-auto">
          <table className="w-full min-w-[980px] border-collapse text-[11.5px]">
            <thead className="sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
              <tr>
                <th className="px-2 py-1.5">Pórtico</th>
                {/* A ação logo depois do pórtico: no celular, a tabela rola de lado, e o
                    alerta do WhatsApp traz a supervisão para cá pelo telefone. */}
                {aoEditar && <th className="px-2 py-1.5">Ação</th>}
                <th className="px-2 py-1.5">Intervalo</th>
                <th className="px-2 py-1.5">Situação</th>
                <th className="px-2 py-1.5">Causa</th>
                <th className="px-2 py-1.5">Aviso ao regulador</th>
                <th className="px-2 py-1.5">Quem</th>
              </tr>
            </thead>
            <tbody>
              {visao.inoperancias.map((x) => (
                <LinhaDaInoperancia key={x.id} x={x} aoEditar={aoEditar} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Painel>
  );
}

function LinhaDaInoperancia({
  x,
  aoEditar,
}: {
  x: InoperanciaNaTela;
  aoEditar: ((e: Edicao) => void) | null;
}) {
  const situacao = ROTULO_DA_INOPERANCIA[x.situacao];
  return (
    <tr className="border-b border-linha-suave align-top">
      <td className="px-2 py-1.5 font-semibold text-tinta">{x.praca}</td>
      {aoEditar && (
        <td className="px-2 py-1.5">
          <div className="flex flex-wrap gap-1">
            {x.situacao === "SUSPEITA" && (
              <>
                <Botao
                  papel="perigo"
                  onClick={() => aoEditar({ tipo: "confirmar", alvo: x })}
                >
                  Confirmar
                </Botao>
                <Botao onClick={() => aoEditar({ tipo: "descartar", alvo: x })}>
                  Descartar
                </Botao>
              </>
            )}
            {x.situacao === "CONFIRMADA" && (
              <Botao onClick={() => aoEditar({ tipo: "confirmar", alvo: x })}>
                Corrigir
              </Botao>
            )}
            {podeAvisar(x) && (
              <Botao
                papel="atencao"
                onClick={() => aoEditar({ tipo: "avisar", alvo: x })}
              >
                Registrar aviso
              </Botao>
            )}
          </div>
        </td>
      )}
      <td className="px-2 py-1.5 whitespace-nowrap text-tinta-2">
        {dataCurta(x.inicio)} → {x.fim ? dataCurta(x.fim) : "em aberto"}
        <span className="block text-[10px] text-tinta-3">
          {duracao(x.duracao_s)}
        </span>
      </td>
      <td className="px-2 py-1.5">
        <Badge cor={situacao.cor}>{situacao.texto}</Badge>
        <span className="mt-0.5 block text-[10px] text-tinta-3">
          {x.origem === "SILENCIO" ? "pelo silêncio do pórtico" : "registrada"}
        </span>
      </td>
      <td className="px-2 py-1.5 text-tinta-2">
        {x.causa_rotulo ?? "—"}
        {x.exclui && (
          <span className="block text-[10px] text-tinta-3">
            fora da conta · {x.evidencia}
          </span>
        )}
        {x.situacao === "DESCARTADA" && x.motivo_do_descarte && (
          <span className="block text-[10px] text-tinta-3">
            {x.motivo_do_descarte}
          </span>
        )}
        {x.observacao && (
          <span className="block text-[10px] text-tinta-3">{x.observacao}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-tinta-2">
        {x.avisado_em ? (
          <>
            {dataCurta(x.avisado_em)}
            {x.protocolo_do_aviso && ` · ${x.protocolo_do_aviso}`}
            <span className="block text-[10px] text-tinta-3">
              {x.avisado_por}
            </span>
          </>
        ) : x.avisar_ate && x.situacao !== "DESCARTADA" ? (
          <span className={avisoPendente(x) ? "text-vermelho" : "text-tinta-3"}>
            {x.critica || x.situacao === "SUSPEITA"
              ? `${x.critica ? "até" : "se crítica, até"} ${dataCurta(x.avisar_ate)}`
              : "não é crítica"}
          </span>
        ) : (
          <span className="text-tinta-3">—</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-tinta-2">
        {x.decidida_por ?? x.aberta_por ?? "o cockpit"}
        <span className="block text-[10px] text-tinta-3">
          {dataEHora(x.decidida_em ?? x.aberta_em)}
        </span>
      </td>
    </tr>
  );
}

const CAMPO =
  "min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta";

function FormularioDaInoperancia({
  edicao,
  visao,
  aoCancelar,
  aoGravar,
}: {
  edicao: Edicao;
  visao: Visao;
  aoCancelar: () => void;
  aoGravar: () => void;
}) {
  const alvo = edicao.tipo === "registrar" ? null : edicao.alvo;
  const porticos = visao.resultado.flatMap((c) =>
    c.porticos.map((p) => ({ id: p.praca_id, nome: `${p.praca} (${c.nome})` })),
  );
  const [escolhido, definirPraca] = useState(porticos[0]?.id ?? "");
  // O pórtico que o filtro tirou da lista não vai ao servidor: vale o que o campo mostra.
  const praca = porticos.some((p) => p.id === escolhido)
    ? escolhido
    : (porticos[0]?.id ?? "");
  // O intervalo como veio, para mandar só o que a pessoa mudou: o campo não tem segundos, e
  // devolver o início "arredondado" mudaria o que o cockpit detectou sem ninguém pedir.
  const inicioDeAntes = alvo ? paraOCampo(alvo.inicio) : "";
  const fimDeAntes = alvo?.fim ? paraOCampo(alvo.fim) : "";
  const [inicio, definirInicio] = useState(inicioDeAntes);
  const [fim, definirFim] = useState(fimDeAntes);
  const [causa, definirCausa] = useState(alvo?.causa ?? "");
  const [critica, definirCritica] = useState(alvo?.critica ?? false);
  const [evidencia, definirEvidencia] = useState(alvo?.evidencia ?? "");
  const [observacao, definirObservacao] = useState(alvo?.observacao ?? "");
  const [motivo, definirMotivo] = useState("");
  const [avisadoEm, definirAvisadoEm] = useState("");
  const [protocolo, definirProtocolo] = useState("");
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [gravando, definirGravando] = useState(false);
  const escolhida: OpcaoDosIndices | undefined = visao.causas.find(
    (c) => c.valor === causa,
  );

  async function gravar() {
    definirRecusa(null);
    definirGravando(true);
    try {
      if (edicao.tipo === "registrar") {
        await api("/tollvision/indices/inoperancias", {
          method: "POST",
          corpo: {
            praca_id: praca,
            inicio: inicio ? paraOServidor(inicio) : null,
            fim: fim ? paraOServidor(fim) : null,
            causa,
            critica,
            evidencia: evidencia || null,
            observacao: observacao || null,
          },
        });
      } else if (edicao.tipo === "confirmar") {
        await api(
          `/tollvision/indices/inoperancias/${edicao.alvo.id}/confirmacao`,
          {
            method: "POST",
            corpo: {
              causa,
              critica,
              inicio:
                inicio && inicio !== inicioDeAntes
                  ? paraOServidor(inicio)
                  : null,
              fim: fim && fim !== fimDeAntes ? paraOServidor(fim) : null,
              evidencia: evidencia || null,
              observacao: observacao || null,
            },
          },
        );
      } else if (edicao.tipo === "descartar") {
        await api(
          `/tollvision/indices/inoperancias/${edicao.alvo.id}/descarte`,
          {
            method: "POST",
            corpo: { motivo },
          },
        );
      } else {
        await api(`/tollvision/indices/inoperancias/${edicao.alvo.id}/aviso`, {
          method: "POST",
          corpo: {
            avisado_em: avisadoEm ? paraOServidor(avisadoEm) : null,
            protocolo: protocolo || null,
          },
        });
      }
      aoGravar();
    } catch (falha) {
      definirRecusa(mensagem(falha, "Não foi possível gravar."));
    } finally {
      definirGravando(false);
    }
  }

  const titulo = {
    registrar: "Registrar inoperância",
    confirmar:
      alvo?.situacao === "CONFIRMADA"
        ? `Corrigir a inoperância — ${alvo.praca}`
        : `Confirmar a inoperância — ${alvo?.praca ?? ""}`,
    descartar: `Descartar a suspeita — ${alvo?.praca ?? ""}`,
    avisar: `Registrar o aviso ao regulador — ${alvo?.praca ?? ""}`,
  }[edicao.tipo];

  // O formulário abre em cima dos índices, e o botão que o abriu está lá embaixo, na tabela:
  // ele vem para a vista, com o foco no primeiro campo.
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    caixa.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    caixa.current
      ?.querySelector<HTMLElement>("select, input")
      ?.focus({ preventScroll: true });
  }, []);

  const pronto =
    edicao.tipo === "descartar"
      ? motivo.trim().length >= 3
      : edicao.tipo === "avisar"
        ? true
        : Boolean(causa) &&
          Boolean(inicio) &&
          (edicao.tipo !== "registrar" || Boolean(praca)) &&
          (!escolhida?.exclui || evidencia.trim().length >= 3);

  return (
    <div ref={caixa}>
      <Painel
        titulo={titulo}
        acoes={
          <Botao onClick={aoCancelar} aria-label="Fechar o formulário">
            <Icone.Fechar />
          </Botao>
        }
      >
        <form
          aria-label={titulo}
          className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            void gravar();
          }}
        >
          {edicao.tipo === "registrar" && (
            <label className="grid gap-1 text-[11px] text-tinta-3">
              Pórtico
              <select
                value={praca}
                onChange={(e) => definirPraca(e.target.value)}
                className={CAMPO}
              >
                {porticos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(edicao.tipo === "registrar" || edicao.tipo === "confirmar") && (
            <>
              <label className="grid gap-1 text-[11px] text-tinta-3">
                Início
                <input
                  type="datetime-local"
                  value={inicio}
                  onChange={(e) => definirInicio(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <label className="grid gap-1 text-[11px] text-tinta-3">
                {edicao.tipo === "registrar" || !alvo?.fim
                  ? "Fim (vazio: em aberto)"
                  : "Fim"}
                <input
                  type="datetime-local"
                  value={fim}
                  onChange={(e) => definirFim(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <label className="grid gap-1 text-[11px] text-tinta-3">
                Causa
                <select
                  value={causa}
                  onChange={(e) => definirCausa(e.target.value)}
                  className={CAMPO}
                >
                  <option value="">Escolha a causa</option>
                  {visao.causas.map((c) => (
                    <option key={c.valor} value={c.valor}>
                      {c.rotulo}
                      {c.exclui ? " — não conta" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-[11px] text-tinta-3 sm:col-span-2">
                Evidência
                {escolhida?.exclui
                  ? " (obrigatória: a causa tira o tempo da conta)"
                  : ""}
                <input
                  value={evidencia}
                  maxLength={500}
                  placeholder="Boletim de ocorrência, protocolo da distribuidora, ofício…"
                  onChange={(e) => definirEvidencia(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <label className="grid gap-1 text-[11px] text-tinta-3 sm:col-span-2">
                Observação
                <input
                  value={observacao}
                  maxLength={500}
                  onChange={(e) => definirObservacao(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <label className="flex items-center gap-2 text-[11.5px] text-tinta-2 sm:col-span-2 xl:col-span-4">
                <input
                  type="checkbox"
                  checked={critica}
                  disabled={alvo?.avisado_em != null}
                  onChange={(e) => definirCritica(e.target.checked)}
                />
                Falha crítica — pede o aviso ao regulador no prazo do contrato
              </label>
              {escolhida?.fonte && (
                <p className="text-[10.5px] text-tinta-3 sm:col-span-2 xl:col-span-4">
                  Fora da disponibilidade por: {escolhida.fonte}.
                </p>
              )}
            </>
          )}
          {edicao.tipo === "descartar" && (
            <label className="grid gap-1 text-[11px] text-tinta-3 sm:col-span-2 xl:col-span-4">
              Por que não era inoperância
              <input
                value={motivo}
                maxLength={300}
                placeholder="O pórtico operava: faltou movimento, faltou dado…"
                onChange={(e) => definirMotivo(e.target.value)}
                className={CAMPO}
              />
            </label>
          )}
          {edicao.tipo === "avisar" && (
            <>
              <label className="grid gap-1 text-[11px] text-tinta-3">
                Avisado em (vazio: agora)
                <input
                  type="datetime-local"
                  value={avisadoEm}
                  onChange={(e) => definirAvisadoEm(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <label className="grid gap-1 text-[11px] text-tinta-3 sm:col-span-2">
                Protocolo do aviso
                <input
                  value={protocolo}
                  maxLength={120}
                  onChange={(e) => definirProtocolo(e.target.value)}
                  className={CAMPO}
                />
              </label>
              <p className="text-[10.5px] text-tinta-3 sm:col-span-2 xl:col-span-4">
                O cockpit não fala com o regulador: registra quem avisou, quando
                e o protocolo. O aviso registrado não muda depois.
              </p>
            </>
          )}
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2 xl:col-span-4">
            <Botao
              type="submit"
              papel={edicao.tipo === "descartar" ? "secundaria" : "primaria"}
              disabled={!pronto || gravando}
            >
              {gravando ? "Gravando…" : "Gravar"}
            </Botao>
            <Botao onClick={aoCancelar}>Cancelar</Botao>
            {recusa && (
              <span role="alert" className="text-[11.5px] text-vermelho">
                {recusa}
              </span>
            )}
          </div>
        </form>
      </Painel>
    </div>
  );
}
